
import dagre from 'dagre';
import { AppNode, Swimlane } from '../types';
import { Edge, Position } from 'reactflow';
import { MIN_LANE_WIDTH, MIN_LANE_HEIGHT } from './layoutConstants';

const LANE_PADDING = 48;

// Layout constants
const PARENT_WIDTH = 220;
const NODE_HEIGHT = 100;
const BOUNDARY_SIZE = 56;
const BOUNDARY_LABEL_H = 20;
const BOUNDARY_GAP = 12;
const PARENT_BOUNDARY_GAP = 30;
const DAGRE_NODESEP = 60;
const DAGRE_RANKSEP = 80;
const RANK_TOLERANCE = 5;
const GRID_SIZE = 15;

function snapToGrid(value: number): number {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

interface BoundingBox {
  width: number;
  height: number;
  offsets: Map<string, { x: number; y: number }>;
}

interface CandidateEdge {
  source: string;
  target: string;
  fromBoundary: boolean;
}

/**
 * Group boundary nodes by their parentNode ID.
 */
function buildBoundaryGroups(nodes: AppNode[]): Map<string, AppNode[]> {
  const groups = new Map<string, AppNode[]>();
  for (const node of nodes) {
    if (node.parentNode) {
      const list = groups.get(node.parentNode) || [];
      list.push(node);
      groups.set(node.parentNode, list);
    }
  }
  return groups;
}

/**
 * Calculate virtual bounding box dimensions and per-boundary offsets
 * for a parent node with attached boundary events.
 */
function calculateBoundingBox(
  boundaries: AppNode[],
  parentWidth: number,
  parentHeight: number
): BoundingBox {
  const count = boundaries.length;

  if (count === 0) {
    return {
      width: parentWidth,
      height: parentHeight,
      offsets: new Map(),
    };
  }

  const boundaryRowWidth = count * BOUNDARY_SIZE + (count - 1) * BOUNDARY_GAP;
  const boxWidth = Math.max(parentWidth, boundaryRowWidth + 20);
  const boxHeight = parentHeight + PARENT_BOUNDARY_GAP + BOUNDARY_SIZE + BOUNDARY_LABEL_H;

  const rowStartX = (parentWidth - boundaryRowWidth) / 2;
  const boundaryY = parentHeight + PARENT_BOUNDARY_GAP;

  const offsets = new Map<string, { x: number; y: number }>();
  for (let i = 0; i < count; i++) {
    const x = rowStartX + i * (BOUNDARY_SIZE + BOUNDARY_GAP);
    offsets.set(boundaries[i].id, { x, y: boundaryY });
  }

  return { width: boxWidth, height: boxHeight, offsets };
}

/**
 * Detect back-edges using DFS from start-event nodes.
 */
function detectBackEdges(
  startIds: string[],
  adjacency: Map<string, string[]>
): Set<string> {
  const backEdges = new Set<string>();
  const visited = new Set<string>();
  const inStack = new Set<string>();

  function dfs(nodeId: string) {
    visited.add(nodeId);
    inStack.add(nodeId);

    for (const neighbor of adjacency.get(nodeId) || []) {
      if (inStack.has(neighbor)) {
        backEdges.add(`${nodeId}->${neighbor}`);
      } else if (!visited.has(neighbor)) {
        dfs(neighbor);
      }
    }

    inStack.delete(nodeId);
  }

  for (const startId of startIds) {
    if (!visited.has(startId)) dfs(startId);
  }
  for (const nodeId of adjacency.keys()) {
    if (!visited.has(nodeId)) dfs(nodeId);
  }

  return backEdges;
}

/**
 * Post-layout: reorder Y positions within ranks so that direct successors
 * of a parent sit above boundary successors. Propagates the reordering
 * through all subsequent ranks via barycenter recalculation.
 */
function correctBoundaryTargetOrdering(
  topLevelIds: Set<string>,
  boundaryGroups: Map<string, AppNode[]>,
  candidateEdges: CandidateEdge[],
  backEdges: Set<string>,
  positions: Map<string, { x: number; y: number }>
): void {
  // Build direct / boundary successor maps
  const directSucc = new Map<string, string[]>();
  const boundarySucc = new Map<string, string[]>();

  for (const edge of candidateEdges) {
    if (backEdges.has(`${edge.source}->${edge.target}`)) continue;
    const map = edge.fromBoundary ? boundarySucc : directSucc;
    const list = map.get(edge.source) || [];
    if (!list.includes(edge.target)) list.push(edge.target);
    map.set(edge.source, list);
  }

  // Identify boundary targets that compete with direct targets from the same parent
  const boundaryTargetIds = new Set<string>();

  let hasInversions = false;
  for (const [parentId] of boundaryGroups) {
    if (!topLevelIds.has(parentId)) continue;
    const dTargets = directSucc.get(parentId) || [];
    const bTargets = boundarySucc.get(parentId) || [];
    if (dTargets.length === 0 || bTargets.length === 0) continue;

    bTargets.forEach((id) => boundaryTargetIds.add(id));

    const dMaxY = Math.max(...dTargets.map((id) => positions.get(id)?.y ?? 0));
    const bMinY = Math.min(...bTargets.map((id) => positions.get(id)?.y ?? 0));
    if (bMinY <= dMaxY) hasInversions = true;
  }

  if (!hasInversions) return;

  // Group nodes into ranks by X proximity
  const sortedByX = [...positions.entries()].sort((a, b) => a[1].x - b[1].x);
  const ranks: Array<{ x: number; nodes: string[] }> = [];
  const nodeToRankIdx = new Map<string, number>();

  for (const [id, pos] of sortedByX) {
    const last = ranks[ranks.length - 1];
    if (last && Math.abs(pos.x - last.x) < RANK_TOLERANCE) {
      last.nodes.push(id);
    } else {
      ranks.push({ x: pos.x, nodes: [id] });
    }
    nodeToRankIdx.set(id, ranks.length - 1);
  }

  // Build predecessor map for barycenter computation
  const predMap = new Map<string, string[]>();
  for (const edge of candidateEdges) {
    if (backEdges.has(`${edge.source}->${edge.target}`)) continue;
    const list = predMap.get(edge.target) || [];
    if (!list.includes(edge.source)) list.push(edge.source);
    predMap.set(edge.target, list);
  }

  // Find earliest rank containing an inverted boundary target
  let firstAffectedIdx = ranks.length;
  for (const bt of boundaryTargetIds) {
    const idx = nodeToRankIdx.get(bt);
    if (idx !== undefined && idx < firstAffectedIdx) firstAffectedIdx = idx;
  }

  // Reorder from the first affected rank through all subsequent ranks
  for (let i = firstAffectedIdx; i < ranks.length; i++) {
    const nodesInRank = ranks[i].nodes;
    if (nodesInRank.length <= 1) continue;

    // Compute barycenter for each node from predecessors' current Y
    const entries = nodesInRank.map((id) => {
      const preds = predMap.get(id) || [];
      let bc: number;
      if (preds.length === 0) {
        bc = positions.get(id)!.y;
      } else {
        bc =
          preds.reduce((s, pid) => s + (positions.get(pid)?.y ?? 0), 0) /
          preds.length;
      }
      // Tiebreaker: boundary targets get a slight downward bias so they
      // sort below direct targets that share the same predecessor
      if (boundaryTargetIds.has(id)) bc += 0.1;
      return { id, bc };
    });

    entries.sort((a, b) => a.bc - b.bc);

    // Collect the original Y slots for this rank (sorted ascending)
    const ySlots = nodesInRank
      .map((id) => positions.get(id)!.y)
      .sort((a, b) => a - b);

    // Reassign Y positions in the new barycenter order
    for (let j = 0; j < entries.length; j++) {
      positions.get(entries[j].id)!.y = ySlots[j];
    }
  }
}

export interface LayoutResult {
  nodes: AppNode[];
  swimlanes: Swimlane[];
}

export const getLayoutedElements = (
  nodes: AppNode[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'LR',
  swimlanes: Swimlane[] = []
): LayoutResult => {
  // ── Phase 1: Analysis ──

  const topLevelNodes: AppNode[] = [];
  const topLevelIds = new Set<string>();

  for (const node of nodes) {
    if (!node.parentNode) {
      topLevelNodes.push(node);
      topLevelIds.add(node.id);
    }
  }

  const boundaryGroups = buildBoundaryGroups(nodes);

  const boundaryToParent = new Map<string, string>();
  for (const node of nodes) {
    if (node.parentNode) {
      boundaryToParent.set(node.id, node.parentNode);
    }
  }

  const startIds = topLevelNodes
    .filter((n) => n.type?.includes('StartEvent'))
    .map((n) => n.id);

  const adjacency = new Map<string, string[]>();
  for (const id of topLevelIds) {
    adjacency.set(id, []);
  }

  // Classify edges, tracking boundary origin for post-layout correction
  const candidateEdges: CandidateEdge[] = [];

  for (const edge of edges) {
    if (edge.sourceHandle === 'boundary') continue;

    const sourceIsBoundary = boundaryToParent.has(edge.source);
    const targetIsBoundary = boundaryToParent.has(edge.target);

    const effectiveSource = sourceIsBoundary
      ? boundaryToParent.get(edge.source)!
      : edge.source;
    const effectiveTarget = targetIsBoundary
      ? boundaryToParent.get(edge.target)!
      : edge.target;

    if (topLevelIds.has(effectiveSource) && topLevelIds.has(effectiveTarget)) {
      if (effectiveSource === effectiveTarget) continue;
      candidateEdges.push({
        source: effectiveSource,
        target: effectiveTarget,
        fromBoundary: sourceIsBoundary,
      });

      const neighbors = adjacency.get(effectiveSource);
      if (neighbors) neighbors.push(effectiveTarget);
    }
  }

  const backEdges = detectBackEdges(startIds, adjacency);

  // Filter and deduplicate for Dagre
  const dagreEdgeSet = new Set<string>();
  const dagreEdges: Array<{ source: string; target: string }> = [];

  for (const edge of candidateEdges) {
    const key = `${edge.source}->${edge.target}`;
    if (backEdges.has(key)) continue;
    if (dagreEdgeSet.has(key)) continue;
    dagreEdgeSet.add(key);
    dagreEdges.push(edge);
  }

  // ── Phase 2: Virtual Bounding Boxes ──

  const boundingBoxes = new Map<string, BoundingBox>();

  for (const node of topLevelNodes) {
    const measuredWidth = node.width ?? PARENT_WIDTH;
    const measuredHeight = node.height ?? NODE_HEIGHT;
    const boundaries = boundaryGroups.get(node.id);
    if (boundaries && boundaries.length > 0) {
      boundingBoxes.set(
        node.id,
        calculateBoundingBox(boundaries, measuredWidth, measuredHeight)
      );
    } else {
      boundingBoxes.set(node.id, {
        width: measuredWidth,
        height: measuredHeight,
        offsets: new Map(),
      });
    }
  }

  // ── Phase 3: Dagre Layout ──

  // compound: true enables setParent, which we use to cluster nodes by
  // swimlane. Without it, setParent throws and the whole layout silently
  // no-ops whenever any lane owns members.
  const dagreGraph = new dagre.graphlib.Graph({ compound: true });
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: DAGRE_NODESEP,
    ranksep: DAGRE_RANKSEP,
  });

  // Register swimlane clusters BEFORE node nodes so dagre can resolve parents.
  // Only lanes that own at least one member node are clustered — lone lanes
  // remain as-is so they don't interfere with the layout geometry.
  const laneByName = new Map<string, Swimlane>();
  const laneMemberIds = new Map<string, string[]>();
  for (const lane of swimlanes) laneByName.set(lane.name, lane);
  for (const node of topLevelNodes) {
    const actor = node.data?.actorType;
    if (actor && laneByName.has(actor)) {
      const lane = laneByName.get(actor)!;
      const list = laneMemberIds.get(lane.id) || [];
      list.push(node.id);
      laneMemberIds.set(lane.id, list);
    }
  }
  for (const [laneId] of laneMemberIds) {
    dagreGraph.setNode(laneId, { label: '' });
  }

  for (const node of topLevelNodes) {
    const box = boundingBoxes.get(node.id)!;
    dagreGraph.setNode(node.id, { width: box.width, height: box.height });
  }

  // Parent relationships — set AFTER both cluster and child nodes exist.
  for (const [laneId, memberIds] of laneMemberIds) {
    for (const memberId of memberIds) {
      dagreGraph.setParent(memberId, laneId);
    }
  }

  for (const edge of dagreEdges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  // ── Phase 3.5: Post-layout Y-order correction ──
  // Reorder nodes within ranks so that direct successors of a parent
  // are above boundary successors, then propagate via barycenter recalc.

  const correctedPositions = new Map<string, { x: number; y: number }>();
  for (const node of topLevelNodes) {
    const dn = dagreGraph.node(node.id);
    if (dn) correctedPositions.set(node.id, { x: dn.x, y: dn.y });
  }

  correctBoundaryTargetOrdering(
    topLevelIds,
    boundaryGroups,
    candidateEdges,
    backEdges,
    correctedPositions
  );

  // ── Phase 3.75: Stack swimlanes vertically ──
  // With rankdir:LR dagre places cluster-lanes side-by-side (as columns).
  // We want swimlanes to read as horizontal rows, so each lane forms a
  // row with left→right flow and lanes stack top-to-bottom. We achieve
  // this by translating each lane cluster AFTER dagre has finished: all
  // members of lane i slide down (and left-align) to sit below lane i-1.
  if (laneMemberIds.size > 0) {
    interface LaneBBox {
      laneId: string;
      memberIds: string[];
      minX: number;
      minY: number;
      maxX: number;
      maxY: number;
    }
    const laneBBoxes: LaneBBox[] = [];
    for (const [laneId, memberIds] of laneMemberIds) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const id of memberIds) {
        const pos = correctedPositions.get(id);
        const box = boundingBoxes.get(id);
        if (!pos || !box) continue;
        minX = Math.min(minX, pos.x - box.width / 2);
        minY = Math.min(minY, pos.y - box.height / 2);
        maxX = Math.max(maxX, pos.x + box.width / 2);
        maxY = Math.max(maxY, pos.y + box.height / 2);
      }
      if (isFinite(minX)) {
        laneBBoxes.push({ laneId, memberIds, minX, minY, maxX, maxY });
      }
    }

    if (laneBBoxes.length > 0) {
      // Preserve the ordering dagre chose (by current Y center) to keep
      // the visual neighbourhood stable.
      laneBBoxes.sort(
        (a, b) => (a.minY + a.maxY) / 2 - (b.minY + b.maxY) / 2
      );

      // Left-align every lane to the leftmost lane's X so the stack has
      // a consistent left edge (matches the horizontal-row reading).
      const sharedMinX = Math.min(...laneBBoxes.map((l) => l.minX));

      // Member spacing between consecutive lanes must be large enough that
      // the lane rectangles (which extend LANE_PADDING beyond their members
      // on every side) don't overlap. Visible gap between lane rectangles
      // = memberSpacing - 2*LANE_PADDING.
      const VISIBLE_LANE_GAP = 60;
      const MEMBER_STACK_GAP = 2 * LANE_PADDING + VISIBLE_LANE_GAP;

      // Track per-node translation so we can propagate it to orphan nodes
      // downstream via incoming edges below.
      const nodeDelta = new Map<string, { dx: number; dy: number }>();

      let cursorY = laneBBoxes[0].minY;
      for (const lane of laneBBoxes) {
        const dx = sharedMinX - lane.minX;
        const dy = cursorY - lane.minY;
        for (const id of lane.memberIds) {
          const pos = correctedPositions.get(id);
          if (!pos) continue;
          pos.x += dx;
          pos.y += dy;
          nodeDelta.set(id, { dx, dy });
        }
        const height = lane.maxY - lane.minY;
        cursorY = lane.minY + dy + height + MEMBER_STACK_GAP;
      }

      // Propagate the translation to orphan nodes (no actorType / not in a
      // lane) via their predecessors — an orphan follows the dy of its
      // upstream source so it stays connected visually. Average the delta
      // when an orphan has multiple known predecessors. Iterate to cover
      // chains of orphans (orphan → orphan → …).
      const predMap = new Map<string, string[]>();
      for (const edge of candidateEdges) {
        if (backEdges.has(`${edge.source}->${edge.target}`)) continue;
        const list = predMap.get(edge.target) || [];
        if (!list.includes(edge.source)) list.push(edge.source);
        predMap.set(edge.target, list);
      }

      let iterations = 0;
      let changed = true;
      while (changed && iterations < topLevelNodes.length + 2) {
        changed = false;
        iterations++;
        for (const node of topLevelNodes) {
          if (nodeDelta.has(node.id)) continue;
          const preds = predMap.get(node.id) || [];
          const knownDeltas = preds
            .map((p) => nodeDelta.get(p))
            .filter((d): d is { dx: number; dy: number } => !!d);
          if (knownDeltas.length === 0) continue;
          const avgDx = knownDeltas.reduce((s, d) => s + d.dx, 0) / knownDeltas.length;
          const avgDy = knownDeltas.reduce((s, d) => s + d.dy, 0) / knownDeltas.length;
          const pos = correctedPositions.get(node.id);
          if (pos) {
            pos.x += avgDx;
            pos.y += avgDy;
            nodeDelta.set(node.id, { dx: avgDx, dy: avgDy });
            changed = true;
          }
        }
      }
    }
  }

  // ── Phase 4: Position Assignment ──

  const laidOutNodes: AppNode[] = nodes.map((node) => {
    if (node.parentNode) {
      const parentBox = boundingBoxes.get(node.parentNode);
      if (parentBox) {
        const offset = parentBox.offsets.get(node.id);
        if (offset) {
          return { ...node, position: { x: snapToGrid(offset.x), y: snapToGrid(offset.y) } };
        }
      }
      return node;
    }

    const pos = correctedPositions.get(node.id);
    if (!pos) return node;

    const box = boundingBoxes.get(node.id)!;

    return {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: {
        x: snapToGrid(pos.x - box.width / 2),
        y: snapToGrid(pos.y - box.height / 2),
      },
    };
  });

  // ── Phase 5: Swimlane Rectangles ──
  // Size each lane to wrap its laid-out member nodes with padding. Lanes
  // with no current members keep their existing rectangle. Then unify
  // lane widths so every lane has the same length — height stays
  // variable (driven by member content).
  const sizedLanes = swimlanes.map((lane) => {
    const members = laidOutNodes.filter(
      (n) => !n.parentNode && n.data?.actorType === lane.name
    );
    if (members.length === 0) {
      return { lane, sized: false as const };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const m of members) {
      const w = m.width ?? boundingBoxes.get(m.id)?.width ?? PARENT_WIDTH;
      const h = m.height ?? boundingBoxes.get(m.id)?.height ?? NODE_HEIGHT;
      minX = Math.min(minX, m.position.x);
      minY = Math.min(minY, m.position.y);
      maxX = Math.max(maxX, m.position.x + w);
      maxY = Math.max(maxY, m.position.y + h);
    }
    const updated: Swimlane = {
      ...lane,
      x: snapToGrid(minX - LANE_PADDING),
      y: snapToGrid(minY - LANE_PADDING),
      width: Math.max(MIN_LANE_WIDTH, snapToGrid(maxX - minX + 2 * LANE_PADDING)),
      height: Math.max(MIN_LANE_HEIGHT, snapToGrid(maxY - minY + 2 * LANE_PADDING)),
    };
    return { lane: updated, sized: true as const };
  });

  // Compute the widest lane; every sized lane inherits that width so all
  // rows share a clean right edge. Unsized (empty) lanes are left alone.
  const commonWidth = Math.max(
    MIN_LANE_WIDTH,
    ...sizedLanes.filter((s) => s.sized).map((s) => s.lane.width)
  );
  const laidOutSwimlanes: Swimlane[] = sizedLanes.map((s) =>
    s.sized ? { ...s.lane, width: commonWidth } : s.lane
  );

  return { nodes: laidOutNodes, swimlanes: laidOutSwimlanes };
};
